import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import OptimusButton from '../../../../common/library/Button/Button';
import { TestIds } from '../../../../assets/testIds';
import { getCanonicalPartnerName } from '../../../../common/constants';
import { toSlug } from '../../../../common/utils/urlUtils';

type ContactSalesDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  partnerName: string;
  partnerCode?: string;
};

export function ContactSalesDialog({
  open,
  onClose,
  onConfirm,
  partnerName,
  partnerCode,
}: ContactSalesDialogProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  // Rename-stable name for deriving a11y ids (see getCanonicalPartnerName).
  const partnerKeyName = getCanonicalPartnerName(partnerCode, partnerName);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-describedby={`${toSlug(partnerKeyName)}-contact-sales-dialog`}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: theme.spacing(84),
          borderRadius: theme.spacing(1.5),
        },
      }}
    >
      <DialogContent sx={{ padding: theme.spacing(3) }}>
        <IconButton
          aria-label={t('button.close') || 'Close'}
          onClick={onClose}
          data-testid={TestIds.partnerDetailsPage.contactSalesDialogCloseBtn}
          sx={{
            position: 'absolute',
            right: theme.spacing(2),
            top: theme.spacing(2),
            color: theme.palette.grey[700],
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(3),
          }}
        >
          <Typography
            variant="h4"
            sx={{ paddingRight: theme.spacing(4) }}
          >
            {t('partnerDetails.contactSales.title')}
          </Typography>
          <Typography
            variant="body8"
            color={theme.palette.grey[700]}
            id={`${toSlug(partnerKeyName)}-contact-sales-dialog`}
            data-testid={TestIds.partnerDetailsPage.contactSalesDialogDescription}
          >
            {t('partnerDetails.contactSales.description')}
            {' '}
            {/* The partner's display name is called out in bold (SN-874) */}
            <Box
              component="span"
              sx={{ fontWeight: 700 }}
              data-testid={TestIds.partnerDetailsPage.contactSalesDialogPartnerName}
            >
              {partnerName || t('partnerDetails.contactSales.descriptionPartnerFallback')}
            </Box>
            {' '}
            {t('partnerDetails.contactSales.descriptionTierRequirement')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <OptimusButton
              customVariant="primary"
              size="m"
              fullWidth={false}
              onClick={onConfirm}
              buttonText={t('button.confirm') || 'Confirm'}
              data-testid={TestIds.partnerDetailsPage.contactSalesConfirmBtn}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
